import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

export const TransferContext = createContext();

const finishedStatuses = new Set([
    "completed",
    "cancelled",
    "error",
]);

let transferSequence = 0;

function createTransferId(type) {
    transferSequence += 1;
    return `${type}-${Date.now()}-${transferSequence}`;
}

export function TransferProvider({ children }) {
    const [transfers, setTransfers] = useState([]);
    const [panelRequest, setPanelRequest] = useState(0);
    const transfersRef = useRef([]);

    useEffect(() => {
        transfersRef.current = transfers;
    }, [transfers]);

    const startTransfer = useCallback((details) => {
        const id = details.id || createTransferId(details.type || "download");
        const transfer = {
            id,
            type: details.type || "download",
            fileKey: details.fileKey || null,
            fileType: details.fileType || "",
            name: details.name || "Untitled transfer",
            status: details.status || "preparing",
            loaded: details.loaded || 0,
            total: details.total || 0,
            message: details.message || "",
            warning: details.warning || "",
            cancel: details.cancel,
            retry: details.retry,
            createdAt: Date.now(),
            updatedAt: Date.now(),
        };

        setTransfers((current) => [transfer, ...current]);
        return id;
    }, []);

    const updateTransfer = useCallback((id, updates) => {
        setTransfers((current) =>
            current.map((transfer) =>
                transfer.id === id
                    ? {
                          ...transfer,
                          ...updates,
                          updatedAt: Date.now(),
                      }
                    : transfer,
            ),
        );
    }, []);

    const completeTransfer = useCallback(
        (id, updates = {}) => {
            updateTransfer(id, {
                status: "completed",
                message: "Download complete",
                warning: "",
                ...updates,
                completedAt: Date.now(),
                cancel: undefined,
            });
        },
        [updateTransfer],
    );

    const failTransfer = useCallback(
        (id, message, retry) => {
            updateTransfer(id, {
                status: "error",
                message,
                warning: "",
                retry,
                cancel: undefined,
                completedAt: Date.now(),
            });
            setPanelRequest((request) => request + 1);
        },
        [updateTransfer],
    );

    const warnTransfer = useCallback(
        (id, warning) => {
            updateTransfer(id, { warning });
            setPanelRequest((request) => request + 1);
        },
        [updateTransfer],
    );

    const cancelTransfer = useCallback(
        (id) => {
            const transfer = transfersRef.current.find(
                (item) => item.id === id,
            );

            if (!transfer || finishedStatuses.has(transfer.status)) {
                return;
            }

            if (transfer.cancel) {
                transfer.cancel();
            }

            updateTransfer(id, {
                status: "cancelled",
                message: "Download cancelled",
                warning: "",
                cancel: undefined,
                completedAt: Date.now(),
            });
        },
        [updateTransfer],
    );

    const retryTransfer = useCallback((id) => {
        const transfer = transfersRef.current.find((item) => item.id === id);

        if (!transfer?.retry) {
            return;
        }

        const retry = transfer.retry;
        setTransfers((current) =>
            current.filter((item) => item.id !== id),
        );
        retry();
    }, []);

    const removeTransfer = useCallback((id) => {
        setTransfers((current) =>
            current.filter((transfer) => transfer.id !== id),
        );
    }, []);

    const clearFinishedTransfers = useCallback(() => {
        setTransfers((current) =>
            current.filter(
                (transfer) => !finishedStatuses.has(transfer.status),
            ),
        );
    }, []);

    const isTransferActive = useCallback((type, fileKey) => {
        return transfersRef.current.some(
            (transfer) =>
                transfer.type === type &&
                transfer.fileKey === fileKey &&
                !finishedStatuses.has(transfer.status),
        );
    }, []);

    const requestPanelOpen = useCallback(() => {
        setPanelRequest((request) => request + 1);
    }, []);

    const hasActiveTransfers = transfers.some(
        (transfer) => !finishedStatuses.has(transfer.status),
    );

    useEffect(() => {
        if (!hasActiveTransfers) {
            return undefined;
        }

        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue =
                "A file transfer is still in progress. Are you sure you want to leave?";
            return event.returnValue;
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () =>
            window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [hasActiveTransfers]);

    const value = useMemo(
        () => ({
            transfers,
            panelRequest,
            startTransfer,
            updateTransfer,
            completeTransfer,
            failTransfer,
            warnTransfer,
            cancelTransfer,
            retryTransfer,
            removeTransfer,
            clearFinishedTransfers,
            isTransferActive,
            requestPanelOpen,
        }),
        [
            transfers,
            panelRequest,
            startTransfer,
            updateTransfer,
            completeTransfer,
            failTransfer,
            warnTransfer,
            cancelTransfer,
            retryTransfer,
            removeTransfer,
            clearFinishedTransfers,
            isTransferActive,
            requestPanelOpen,
        ],
    );

    return (
        <TransferContext.Provider value={value}>
            {children}
        </TransferContext.Provider>
    );
}

export function useTransfers() {
    return useContext(TransferContext);
}
